import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Reveal } from "./Reveal";
import { useSimplify } from "./simplify-context";
import { ArrowRight } from "lucide-react";

export const FAQS = [
  {
    q: "How long does it take to get set up?",
    a: "Most teams see their first connected view within two to four weeks. We start with one job to be done — for example, sold versus delivered — prove the value, then widen from there.",
  },
  {
    q: "Do we have to change the tools we already use?",
    a: "No. Everything works inside the tools your business already runs on — we connect them so they behave like one simple system. Your team's daily habits stay; the chasing goes.",
  },
  {
    q: "Is our business data secure?",
    a: "Your data remains in your own systems, under your control. Access is role-based throughout, so each person sees only what their role requires — nothing more.",
  },
  {
    q: "Will our team actually use it?",
    a: "That's the design goal. Nobody learns a new system. Each person gets exactly the view their role needs — a foreman sees today's tasks, an owner sees sold versus delivered — so adoption feels like relief, not training.",
  },
  {
    q: "What does 'ask your business a question' actually mean?",
    a: "You type a plain question — 'which jobs are at risk this month?' or 'what did we sell versus deliver last quarter?' — and get a clear answer drawn from your live CRM, projects, and books.",
  },
  {
    q: "What does it cost?",
    a: "Every engagement is scoped to the business, and it starts with a free simplification review — a calm look at where your team's time is going and what connecting your systems would return.",
  },
];

export default function FAQSection() {
  const { setOpen } = useSimplify();

  return (
    <section id="faq" className="py-20 lg:py-32 bg-white border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-12 gap-8 lg:gap-12">
        <Reveal className="col-span-12 lg:col-span-4">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-4">
            Questions, answered plainly
          </p>
          <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-brand-navy text-2xl sm:text-3xl lg:text-4xl mb-6">
            Everything owners usually ask us first.
          </h2>
          <p className="text-base text-slate-600 leading-relaxed mb-8">
            Something more specific on your mind? The simplification review is
            the easiest place to ask it.
          </p>
          <button
            data-testid="faq-cta-button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2.5 rounded-full bg-brand-navy text-white text-sm font-semibold px-7 py-3.5 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
          >
            Show me where I can simplify
            <ArrowRight size={16} />
          </button>
        </Reveal>

        <Reveal delay={0.15} className="col-span-12 lg:col-span-7 lg:col-start-6">
          <Accordion
            type="single"
            collapsible
            data-testid="faq-accordion"
            className="w-full"
          >
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b border-slate-200"
              >
                <AccordionTrigger
                  data-testid={`faq-question-${i}`}
                  className="text-left font-display font-medium text-base sm:text-lg text-brand-navy hover:text-brand-teal py-6 transition-colors"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`faq-answer-${i}`}
                  className="text-sm sm:text-base text-slate-600 leading-relaxed pb-6"
                >
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}
